import { useState } from "react";
import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import type { EntityFragment, IObject } from "@/__generated__/graphql";
import { Select } from "@/components/molecules/Select";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";

import {
  useAcceptExternalGrant,
  useAcceptInvitation,
  useDeclineExternalGrant,
  useDeclineInvitation,
} from "./Invitations.hooks";

import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Card } from "@/components/ui/card";

type PendingInvitation = {
  identifier: string;
  entity?: {
    name?: string | null;
  } | null;
};

/** A business's books someone has asked to share with the person, as their accountant. */
type PendingExternalGrant = {
  identifier: string;
  /** The business whose books they are - or the sharer's own books. */
  entityName: string;
  isOwnBooks: boolean;
  sharedByName?: string | null;
  sharedByEmailAddress: string;
  /** The verified firm they were asked of itself - none asked of a person. */
  granteeIdentifier?: string | null;
  granteeName?: string | null;
};

type PendingInvitationsListProps = {
  invitations: PendingInvitation[];
  externalGrants?: PendingExternalGrant[];
  highlightedAccountIdentifier?: string | null;
  showHeading?: boolean;
  onChanged?: () => void;
};

export function PendingInvitationsList({
  invitations,
  externalGrants = [],
  highlightedAccountIdentifier = null,
  showHeading = true,
  onChanged,
}: PendingInvitationsListProps) {
  const { accept, loading: acceptLoading } = useAcceptInvitation();
  const { decline, loading: declineLoading } = useDeclineInvitation();
  const { accept: acceptGrant, loading: acceptGrantLoading } =
    useAcceptExternalGrant();
  const { decline: declineGrant, loading: declineGrantLoading } =
    useDeclineExternalGrant();
  const navigate = useNavigate();

  if (invitations.length === 0 && externalGrants.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {showHeading && (
        <Typography level="title-md">Pending invitations</Typography>
      )}
      {invitations.map((invitation) => {
        const isHighlighted =
          highlightedAccountIdentifier != null &&
          invitation.identifier === highlightedAccountIdentifier;

        return (
          <Card
            key={invitation.identifier}
            data-testid={`pending-invitation-${invitation.identifier}`}
            className={cn(
              "flex flex-row items-center justify-between p-4",
              isHighlighted && "border-ring shadow-sm",
            )}
          >
            <div>
              <Typography level="title-sm">
                {invitation.entity
                  ? invitation.entity.name
                  : "No business found"}
              </Typography>
              <Typography level="body-xs" className="text-muted-foreground/80">
                You have been invited to join this organization
              </Typography>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await accept(invitation.identifier);
                  onChanged?.();
                }}
                disabled={acceptLoading}
              >
                {acceptLoading ? <Spinner /> : <CheckIcon size="1em" />}Accept
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={async () => {
                  await decline(invitation.identifier);
                  onChanged?.();
                }}
                disabled={declineLoading}
                className="text-destructive hover:text-destructive"
              >
                {declineLoading ? <Spinner /> : <XIcon size="1em" />}
              </Button>
            </div>
          </Card>
        );
      })}
      {externalGrants.map((grant) => {
        const isHighlighted =
          highlightedAccountIdentifier != null &&
          grant.identifier === highlightedAccountIdentifier;

        return (
          <PendingExternalGrantCard
            key={grant.identifier}
            grant={grant}
            isHighlighted={isHighlighted}
            isAccepting={acceptGrantLoading}
            isDeclining={declineGrantLoading}
            onAccept={async (entityIdentifier) => {
              await acceptGrant(grant.identifier, entityIdentifier);
              onChanged?.();
              // Taken up, the client is among the firm's: on to them.
              navigate("/app/clients");
            }}
            onDecline={async () => {
              await declineGrant(grant.identifier);
              onChanged?.();
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Books asked of the person: taken up for their firm - a business of theirs
 * they own, picked here - or turned down. Owning none, they make one, and the
 * books are taken up for it as it is made.
 */
function PendingExternalGrantCard({
  grant,
  isHighlighted,
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
}: {
  grant: PendingExternalGrant;
  isHighlighted: boolean;
  isAccepting: boolean;
  isDeclining: boolean;
  onAccept: (entityIdentifier: string) => Promise<void>;
  onDecline: () => Promise<void>;
}) {
  const { accounts } = useScope();
  const navigate = useNavigate();
  // The accounting or bookkeeping firms they own: any other business keeps
  // only its own books. Asked of a firm itself, that firm alone.
  const firms = (accounts ?? [])
    .filter((account) => account.isOwner && account.isEntityAccount)
    .map((account) => account.entity)
    .filter(
      (entity): entity is EntityFragment =>
        entity != null &&
        entity.isAccountingFirm &&
        (grant.granteeIdentifier == null ||
          entity.identifier === grant.granteeIdentifier),
    );
  // The firm they are taken up for: the one they were asked of, where the
  // server names it, else picked - never guessed for them.
  const [picked, setPicked] = useState<string | null>(null);
  const firm =
    firms.find(
      (entity) => entity.identifier === (picked ?? grant.granteeIdentifier),
    ) ?? null;
  const books = grant.isOwnBooks
    ? "their books"
    : `${grant.entityName}'s books`;

  return (
    <Card
      data-testid={`pending-external-grant-${grant.identifier}`}
      className={cn(
        "flex flex-row items-center justify-between p-4",
        isHighlighted && "border-ring shadow-sm",
      )}
    >
      <div>
        <Typography level="title-sm">
          {grant.sharedByName
            ? `${grant.sharedByName} (${grant.sharedByEmailAddress})`
            : grant.sharedByEmailAddress}
        </Typography>
        <Typography level="body-xs" className="text-muted-foreground/80">
          {grant.granteeIdentifier != null
            ? `Has shared ${books} with ${grant.granteeName}, as their accountant: take them up for it`
            : firms.length > 0
              ? `Has shared ${books} with you, as their accountant: take them up for your firm`
              : `Has shared ${books} with you, as their accountant: make your accounting or bookkeeping firm to take them up for it - or say a business of yours is one, in its settings`}
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        {firms.length > 0 ? (
          <>
            <div className="w-50">
              <Select<EntityFragment>
                value={firm}
                onChange={(entity) => setPicked(entity.identifier)}
                data={firms}
                getOptionKey={(entity) => entity.identifier}
                size="sm"
                placement="bottom-end"
              >
                <Select.Trigger placeholder="Pick your firm">
                  {firm ? `For ${firm.name}` : null}
                </Select.Trigger>
                <Select.Options<EntityFragment>>
                  {(entity) => (
                    <Select.Option
                      value={entity}
                      data-testid={`pending-external-grant-firm-${grant.identifier}-${entity.identifier}`}
                    >
                      {entity.name}
                    </Select.Option>
                  )}
                </Select.Options>
              </Select>
            </div>
            <Button
              size="sm"
              disabled={isAccepting || !firm}
              data-testid={`pending-external-grant-accept-${grant.identifier}`}
              onClick={() => firm && void onAccept(firm.identifier)}
            >
              {isAccepting ? <Spinner /> : <CheckIcon size="1em" />}Accept
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Made where the invitations are answered - not over them, as
              // the first thing someone signing in is asked.
              navigate(`/app/invitations?external_grant=${grant.identifier}`);
              State.getState().pushToCommandStack({
                commandDefinitionId: "create-EntityObject",
                context: {
                  initialFieldValues: { isAccountingFirm: true },
                  onCreated: (firm: IObject) => void onAccept(firm.identifier),
                },
              });
            }}
          >
            <PlusIcon size="1em" />
            New firm
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Decline"
          onClick={() => void onDecline()}
          disabled={isDeclining}
          className="text-destructive hover:text-destructive"
        >
          {isDeclining ? <Spinner /> : <XIcon size="1em" />}
        </Button>
      </div>
    </Card>
  );
}
